import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { baseURL } from "../../utils/api";

const RECENT_CONVERSATIONS_LIMIT = 10;

function formatTime(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function Transcript({ state }) {
  if (!state || state.status === "loading") {
    return <p className="account-logs-empty">Loading…</p>;
  }
  if (state.status === "error") {
    return <p className="account-logs-empty">Couldn't load this conversation. Please try again later.</p>;
  }
  if (state.turns.length === 0) {
    return <p className="account-logs-empty">The transcript for this conversation is not available.</p>;
  }
  // Fragment, not a wrapper div: the question's right-alignment (align-self) only works when it
  // and the reply are direct children of the transcript's flex container.
  return state.turns.map((turn, i) => (
    <React.Fragment key={i}>
      <div className="account-conv-question">{turn.question}</div>
      <div className="account-conv-reply">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{turn.replyText}</ReactMarkdown>
      </div>
    </React.Fragment>
  ));
}

// The signed-in user's most recent conversations, newest first — the account-page counterpart
// of the admin conversations list. A row expands in place to show its transcript, fetched
// lazily (and cached) the first time it's opened.
export default function ConversationHistory() {
  const [conversations, setConversations] = useState(null); // null = still loading
  const [loadFailed, setLoadFailed] = useState(false);
  const [openIds, setOpenIds] = useState(() => new Set()); // rows can be expanded independently
  const [transcripts, setTranscripts] = useState({}); // conversationId -> { status, turns }

  useEffect(() => {
    let cancelled = false;
    api.get(`${baseURL}/authenticated/copilot/conversations`, { params: { limit: RECENT_CONVERSATIONS_LIMIT } })
      .then(({ data }) => {
        if (!cancelled) setConversations(data?.conversations ?? []);
      })
      .catch((err) => {
        console.error("Failed to fetch conversations:", err);
        if (!cancelled) setLoadFailed(true);
      });
    return () => { cancelled = true; };
  }, []);

  const toggle = (conversationId) => {
    const wasOpen = openIds.has(conversationId);
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (wasOpen) next.delete(conversationId);
      else next.add(conversationId);
      return next;
    });
    if (wasOpen) return;

    // Already fetched (or being fetched): reuse it. A previous error falls through to retry.
    const status = transcripts[conversationId]?.status;
    if (status === "ready" || status === "loading") return;

    setTranscripts((prev) => ({ ...prev, [conversationId]: { status: "loading" } }));
    api.get(`${baseURL}/authenticated/copilot/conversation`, { params: { id: conversationId } })
      .then(({ data }) => {
        setTranscripts((prev) => ({ ...prev, [conversationId]: { status: "ready", turns: data?.turns ?? [] } }));
      })
      .catch((err) => {
        console.error("Failed to fetch conversation:", err);
        setTranscripts((prev) => ({ ...prev, [conversationId]: { status: "error" } }));
      });
  };

  let body;
  if (loadFailed) {
    body = <p className="account-logs-empty">Couldn't load your conversations. Please try again later.</p>;
  } else if (conversations === null) {
    body = <p className="account-logs-empty">Loading…</p>;
  } else if (conversations.length === 0) {
    body = <p className="account-logs-empty">No conversations yet — ask askTheSea a question to get started.</p>;
  } else {
    body = (
      <div className="account-logs">
        {conversations.map((c) => {
          const isOpen = openIds.has(c.conversationId);
          return (
            <React.Fragment key={c.conversationId}>
              <div className="account-log-item">
                <div className="account-conv-main">
                  <div className="account-log-question account-conv-title">{c.firstQuestion || "Conversation"}</div>
                  <span className="account-conv-meta">{formatTime(c.createdTime)}</span>
                </div>
                <button type="button" className="account-conv-toggle" onClick={() => toggle(c.conversationId)} aria-expanded={isOpen}>
                  {isOpen ? "hide" : "view"}
                </button>
              </div>
              {isOpen && (
                <div className="account-conv-transcript">
                  <Transcript state={transcripts[c.conversationId]} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  return (
    <section className="tight">
      <div className="account-conv-header">
        <h2>Recent conversations</h2>
        {openIds.size >= 2 && (
          <button type="button" className="account-conv-toggle" onClick={() => setOpenIds(new Set())}>
            hide all
          </button>
        )}
      </div>
      {body}
    </section>
  );
}
